/**
 * SoilGroupingPage — the soil grouping builder (U2).
 *
 * Replaces a screen whose assign affordance was quadratic: one "→N" button per
 * orchard PER GROUP (39 orchards x 6 groups = 234 buttons). Here you select
 * orchard rows and assign the selection to one group.
 *
 * Deliberately NOT wrapped in <FormShell>: .pat-form-shell__content is capped at
 * max-width 780px in both long and short modes, which would cramp a 7-column
 * roster table. <SaveBar> is self-contained (position: sticky; bottom: 0) and
 * does not need FormShell's grid slot.
 *
 * All grouping decisions live in ./soilGrouping — this file is presentation.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import {
  DataTable, FilterBar, Menu, MenuItem, Modal, Notice, SaveBar, StatusPill,
} from '../patterns';
import type { SaveBarStatus, SortState } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { soilSessionService, SoilSession, SoilSessionFact, Operator } from '../../services/soilSessionService';
import { soilGroupSetService, SoilGroupSet, ApplySummary } from '../../services/soilGroupSetService';
import { formatMeasure } from '../../utils/formatMeasure';
import { generateUUID } from '../../utils';
import { naturalCompare } from '../../utils/naturalCompare';
import {
  DraftGroup, OrchardRow, buildRows, rangeSelect, assignToGroup, groupTotals, removeGroup,
  findBlockers, canConfirm, autoBalance, unassignedNames,
} from './soilGrouping';
import './nutrition.css';

const GROUP_COLOURS = ['#1E40AF', '#065F46', '#9D174D', '#5B21B6', '#92400E', '#155E75', '#B91C1C', '#3730A3'];
export const groupColour = (index: number): string => GROUP_COLOURS[index % GROUP_COLOURS.length];

// A sentinel bulk-assign target meaning "remove from every group". Never a real
// group uid — uids are generateUUID() values (canonical UUID format) — so this
// literal cannot collide with one. assignToGroup() already treats any unknown
// targetUid as "drop from current group, add to none" (pinned by a
// soilGrouping.test.ts case), so no new grouping logic is needed to support it.
const UNASSIGN_TARGET = '__unassign__';

// Cultivar chip ids are prefixed so a cultivar literally named "all" or
// "unassigned" cannot collide with the two reserved chip ids below.
const CULTIVAR_CHIP_PREFIX = 'cv:';

const toDraft = (g: SoilSession['groups'][0]): DraftGroup => ({
  uid: g.groupId,
  label: g.label || g.groupId,
  orchardNames: g.orchardNames || [],
  operators: (g.assignedOperators || []).map((o) => ({ operatorId: o.operatorId, name: o.name })),
});

const SoilGroupingPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<SoilSession | null>(null);
  const [facts, setFacts] = useState<SoilSessionFact[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [groups, setGroups] = useState<DraftGroup[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [target, setTarget] = useState<string>('');
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState<'all' | 'unassigned' | string>('all');
  // Default: orchard name, natural order (so "B9" sorts before "B10") — the
  // roster's primary identifying column, and the order an unsorted table was
  // already presenting it in.
  const [sort, setSort] = useState<SortState>({ key: 'orchardName', direction: 'asc' });
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Apply-a-set / copy-from-a-session — both go through ONE dry-run preview
  // flow so the two paths can never diverge (see openPreview below). `sets`
  // and `copySources` are loaded best-effort: a failed list load must not
  // brick this page, it should just leave the corresponding menu empty.
  type PreviewChoice = { kind: 'set'; id: string; label: string } | { kind: 'copy'; id: string; label: string };
  const [sets, setSets] = useState<SoilGroupSet[]>([]);
  const [copySources, setCopySources] = useState<SoilSession[]>([]);
  const [preview, setPreview] = useState<PreviewChoice | null>(null);
  const [summary, setSummary] = useState<ApplySummary | null>(null);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyError, setApplyError] = useState('');

  // Hooks must sit above every early return — a hook below a guard changes hook
  // order between renders and React throws.
  // The Soil crumb MUST carry ?program= — this page is a sibling of the
  // workspace, not one of its tabs, so the layout re-resolves the programme as
  // "?program= if valid, else the tenant's ACTIVE programme". A bare
  // /nutrition/soil drops it and returns the user to a different board.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    {
      label: t('nutrition.soil.workspace.title'),
      to: session ? `/nutrition/soil?program=${session.soilProgramId}` : '/nutrition/soil',
    },
    { label: session ? `${session.productName} · ${session.month}` : t('nutrition.soil.grouping2.crumb') },
  ]);
  // A user who has assigned dozens of orchards across several groups and then
  // hits F5 (or closes the tab) would otherwise lose everything silently.
  useBeforeUnloadWarning(dirty);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      try {
        const { session: s, facts: f } = await soilSessionService.get(id);
        if (cancelled) return;
        // Only a draft is editable here (the backend enforces NOT_DRAFT anyway);
        // anything else belongs on the session detail screen.
        if (s.status !== 'draft') { navigate(`/nutrition/soil/sessions/${id}`, { replace: true }); return; }
        setSession(s);
        setFacts(f);
        setGroups((s.groups || []).map(toDraft));
        setError('');
        // Copy sources exclude the session itself and any session with zero
        // groups — a session with no groups is not a copy source, offering
        // it would guarantee an empty result. Sorted month descending.
        soilSessionService.listForProgram(s.soilProgramId)
          .then((list) => {
            if (cancelled) return;
            setCopySources(
              list.filter((x) => x._id !== s._id && (x.groups || []).length > 0)
                .sort((a, b) => b.month.localeCompare(a.month)),
            );
          })
          .catch(() => { if (!cancelled) setCopySources([]); });
      } catch (_e) {
        if (!cancelled) setError(t('nutrition.soil.grouping2.loadFailed'));
      }
    })();
    soilSessionService.listOperators().then((o) => { if (!cancelled) setOperators(o); }).catch(() => setOperators([]));
    soilGroupSetService.list().then((s) => { if (!cancelled) setSets(s); }).catch(() => { if (!cancelled) setSets([]); });
    return () => { cancelled = true; };
    // `t` is deliberately absent: react-i18next's t is not referentially stable and
    // would re-fire this effect every render, clobbering the grouping in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const rows: OrchardRow[] = useMemo(
    () => (session ? buildRows(session.orchards || [], facts, groups) : []),
    [session, facts, groups],
  );

  const cultivars = useMemo(
    () => Array.from(new Set(rows.map((r) => r.cultivar).filter(Boolean))).sort(),
    [rows],
  );

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.orchardName.toLowerCase().includes(q) && !r.cultivar.toLowerCase().includes(q)) return false;
      if (chip === 'unassigned' && r.groupUid !== null) return false;
      // Cultivar chips carry the `cv:` prefix (CULTIVAR_CHIP_PREFIX); strip it
      // before comparing against the raw cultivar on the row.
      if (chip !== 'all' && chip !== 'unassigned' && r.cultivar !== chip.slice(CULTIVAR_CHIP_PREFIX.length)) return false;
      return true;
    });
  }, [rows, search, chip]);

  // rangeSelect operates on DISPLAYED order — post-filter AND post-sort (see
  // soilGrouping.ts) — so orderedNames must be derived from the SORTED rows,
  // never from `shown` directly. Sorting before computing it is what keeps a
  // shift-click correct after the user has clicked a column header.
  const sortedShown = useMemo(() => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    const arr = shown.slice();
    arr.sort((a, b) => {
      switch (sort.key) {
        case 'cultivar': return naturalCompare(a.cultivar, b.cultivar) * dir;
        case 'treeCount': return (a.treeCount - b.treeCount) * dir;
        case 'areaHa': return (a.areaHa - b.areaHa) * dir;
        case 'kg': return (a.kg - b.kg) * dir;
        default: return naturalCompare(a.orchardName, b.orchardName) * dir;
      }
    });
    return arr;
  }, [shown, sort]);

  const orderedNames = useMemo(() => sortedShown.map((r) => r.orchardName), [sortedShown]);

  const onToggle = useCallback((name: string, shiftKey: boolean) => {
    setSelected((prev) => {
      if (shiftKey && anchor) return rangeSelect(prev, orderedNames, anchor, name);
      return prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name];
    });
    setAnchor(name);
  }, [anchor, orderedNames]);

  // setGroups/setTarget are called as two separate top-level statements (not
  // nested) because React invokes state updaters twice under StrictMode —
  // calling setTarget from inside the setGroups updater would not be pure.
  // The uid is generated outside the updater (it must be identical across a
  // possible double-invoke), but the label's `n` is derived from `prev`
  // *inside* the updater — reading it from the `groups` closure instead would
  // let two same-batch addGroup() calls both compute the same `n` and mint two
  // groups both labelled "Group 1"; t() is a pure read so calling it inside the
  // updater is safe. Intent preserved: a newly added group becomes the
  // bulk-assign target only when no target is set yet.
  const addGroup = () => {
    const uid = generateUUID();
    setGroups((prev) => [...prev, {
      uid, label: t('nutrition.soil.grouping2.groupN', { n: prev.length + 1 }), orchardNames: [], operators: [],
    }]);
    setTarget((cur) => cur || uid);
    setDirty(true);
  };

  const applyBulk = () => {
    if (!target || !selected.length) return;
    setGroups((prev) => assignToGroup(prev, selected, target));
    setSelected([]);
    setAnchor(null);
    setDirty(true);
  };

  const renameGroup = (uid: string, label: string) => {
    setGroups((prev) => prev.map((g) => (g.uid === uid ? { ...g, label } : g)));
    setDirty(true);
  };

  const dropGroup = (uid: string) => {
    // Only the group is removed; its orchards fall back to unassigned because
    // `rows` derives assignment from `groups`. They must never be lost.
    setGroups((prev) => removeGroup(prev, uid));
    setTarget((cur) => (cur === uid ? '' : cur));
    setDirty(true);
  };

  const addOperator = (uid: string, operatorId: string) => {
    if (!operatorId) return;
    const op = operators.find((o) => o.operatorId === operatorId);
    if (!op) return;
    setGroups((prev) => prev.map((g) => (
      g.uid !== uid || g.operators.length >= 4 || g.operators.some((o) => o.operatorId === operatorId)
        ? g
        : { ...g, operators: [...g.operators, { operatorId: op.operatorId, name: op.name }] }
    )));
    setDirty(true);
  };

  const removeOperator = (uid: string, operatorId: string) => {
    setGroups((prev) => prev.map((g) => (
      g.uid === uid ? { ...g, operators: g.operators.filter((o) => o.operatorId !== operatorId) } : g
    )));
    setDirty(true);
  };

  // A blank/whitespace-only group name must never reach the server or a
  // blocker/chip/select label — it would otherwise round-trip as a raw UUID
  // (toDraft's `g.label || g.groupId` fallback) or produce a garbled join like
  // ", Group 2" in the blocker notice. Falls back to the exact "Group N"
  // convention addGroup() already uses, keyed off display position, so a
  // blanked-out name reads exactly like a freshly-added group would. Not
  // wrapped in useMemo/useCallback: it reads `t`, and `t` must never sit in a
  // memo/effect dependency array — this is cheap enough to recompute freely.
  const labelFor = (g: DraftGroup, i: number): string => (g.label.trim() ? g.label : t('nutrition.soil.grouping2.groupN', { n: i + 1 }));

  // Also not memoized, for the same reason (labelFor reads `t`).
  const blockers = findBlockers(groups.map((g, i) => ({ ...g, label: labelFor(g, i) })));
  const ungrouped = useMemo(
    () => (session ? unassignedNames(session.orchards || [], groups) : []),
    [session, groups],
  );

  const toPayload = (gs: DraftGroup[]) => gs.map((g, i) => ({
    groupId: g.uid,
    label: labelFor(g, i),
    orchardNames: g.orchardNames,
    assignedOperators: g.operators.map((o) => ({ operatorId: o.operatorId, name: o.name })),
  }));

  // A rejection from saveGroups/plan is often not "try again" — another
  // manager may have planned or cancelled this session while it was open here,
  // in which case every retry would keep failing with the same generic message
  // forever. Re-fetch to find out: if the session has moved on, say so
  // specifically and send the user to the page that reflects reality; only
  // fall back to the generic message when it is genuinely still a draft — or
  // when the re-fetch itself fails, in which case we must not throw from
  // inside a catch block.
  const handleSaveConflict = async (sessionId: string, genericMessage: string) => {
    try {
      const { session: fresh } = await soilSessionService.get(sessionId);
      if (fresh.status !== 'draft') {
        setSaveError(t('nutrition.soil.grouping2.conflictMoved'));
        navigate(`/nutrition/soil/sessions/${sessionId}`);
        return;
      }
      setSaveError(genericMessage);
    } catch (_e2) {
      setSaveError(genericMessage);
    }
  };

  const onSaveDraft = async () => {
    if (!id) return;
    setSaving(true); setSaveError('');
    try {
      await soilSessionService.saveGroups(id, toPayload(groups));
      setDirty(false);
    } catch (_e) {
      await handleSaveConflict(id, t('nutrition.soil.grouping2.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const onConfirm = async () => {
    if (!id || !canConfirm(blockers)) return;
    setSaving(true); setSaveError('');
    try {
      // Single-call save-then-plan: a rejected plan can never leave persisted
      // groups this screen's local state does not know about.
      await soilSessionService.plan(id, toPayload(groups));
      setDirty(false);
      navigate(`/nutrition/soil/sessions/${id}`);
    } catch (_e) {
      await handleSaveConflict(id, t('nutrition.soil.grouping2.confirmFailed'));
    } finally {
      setSaving(false);
    }
  };

  const runAutoBalance = () => {
    setGroups((prev) => autoBalance(prev, buildRows(session?.orchards || [], facts, prev), facts));
    setDirty(true);
  };

  // Shared dry-run opener for BOTH "apply a set" and "copy from a session" —
  // one function so the two paths can never diverge. The modal opens only
  // once the summary is in hand; if the dry run itself fails, no modal is
  // shown at all, only an error notice.
  const openPreview = async (choice: PreviewChoice) => {
    if (!id) return;
    setApplyBusy(true);
    setApplyError('');
    try {
      const res = choice.kind === 'set'
        ? await soilGroupSetService.apply(id, choice.id, { dryRun: true })
        : await soilGroupSetService.copyFrom(id, choice.id, { dryRun: true });
      setSummary(res.summary);
      setPreview(choice);
    } catch (_e) {
      setApplyError(t('nutrition.soil.groupSets.previewFailed'));
    } finally {
      setApplyBusy(false);
    }
  };

  // The modal's primary action is the ONLY thing that writes.
  const confirmPreview = async () => {
    if (!id || !preview) return;
    setApplyBusy(true);
    setApplyError('');
    try {
      const res = preview.kind === 'set'
        ? await soilGroupSetService.apply(id, preview.id, { dryRun: false })
        : await soilGroupSetService.copyFrom(id, preview.id, { dryRun: false });
      if (res.session) {
        setSession(res.session);
        setGroups((res.session.groups || []).map(toDraft));
        // The server has just persisted exactly these groups, so leaving
        // `dirty` true would trigger a bogus unload warning.
        setDirty(false);
        setSelected([]);
        setAnchor(null);
      }
      setPreview(null);
      setSummary(null);
    } catch (_e) {
      setApplyError(t('nutrition.soil.groupSets.applyFailed'));
      setPreview(null);
      setSummary(null);
    } finally {
      setApplyBusy(false);
    }
  };

  const selectedKg = selected.reduce((s, n) => s + (rows.find((r) => r.orchardName === n)?.kg || 0), 0);
  // Selection intentionally survives a search/filter change (so selecting five
  // orchards then searching for a sixth doesn't lose the five) — `selectedKg`
  // above sums over the full `rows`, not just `shown`, for the same reason.
  // But that means the bulk bar can silently include orchards not currently on
  // screen, so surface the count that's hidden rather than pruning it.
  const hiddenSelectedCount = selected.filter((n) => !orderedNames.includes(n)).length;

  // Error branch returns BEFORE the loading guard, so a failed load renders a
  // notice rather than sitting on "loading" forever.
  if (error) return <div className="pat-page" data-testid="grp-error"><Notice tone="danger">{error}</Notice></div>;
  if (!session) return <div className="pat-page">{t('common.loading')}</div>;

  const headerCheckedAll = shown.length > 0 && shown.every((r) => selected.includes(r.orchardName));
  // Hoisted out of the group-summary map below — the brief computed this inside
  // the map, recomputing every group's totals once per group; doing it once here
  // is a behaviour-preserving cleanup. Floored at 1 so the bar width never divides
  // by zero when every group is still empty.
  const heaviestKg = Math.max(1, ...groups.map((x) => groupTotals(x, facts).kg));

  return (
    <div className="pat-page" data-testid="soil-grouping-page">
      <PageHeader
        title={`${session.productName} · ${session.month}`}
        meta={(
          <>
            <StatusPill variant="warn">{t('nutrition.soil.board.status.draft')}</StatusPill>{' '}
            {t('nutrition.soil.grouping2.meta', {
              orchards: (session.orchards || []).length,
              groups: groups.length,
              kg: formatMeasure(rows.reduce((s, r) => s + r.kg, 0)),
            })}
          </>
        )}
        actions={(
          <>
            {/* A menu that can only ever report "0 assigned" is worse than no
                menu, so both are disabled when the session has no orchards
                to group, and while a request is in flight. */}
            <Menu
              trigger={(
                <button
                  type="button" className="pat-btn" data-testid="grp-apply-set"
                  disabled={applyBusy || !sets.length || !(session.orchards || []).length}
                >
                  {t('nutrition.soil.groupSets.applySet')}
                </button>
              )}
            >
              {sets.map((s) => (
                <MenuItem key={s._id} onClick={() => openPreview({ kind: 'set', id: s._id, label: s.name })}>
                  {s.name}
                </MenuItem>
              ))}
            </Menu>
            <Menu
              trigger={(
                <button
                  type="button" className="pat-btn" data-testid="grp-copy-from"
                  disabled={applyBusy || !copySources.length || !(session.orchards || []).length}
                >
                  {t('nutrition.soil.groupSets.copyFrom')}
                </button>
              )}
            >
              {copySources.map((s) => (
                <MenuItem key={s._id} onClick={() => openPreview({ kind: 'copy', id: s._id, label: `${s.productName} · ${s.month}` })}>
                  {s.productName} · {s.month}
                </MenuItem>
              ))}
            </Menu>
            <button type="button" className="pat-btn" data-testid="grp-autobalance" disabled={!groups.length} onClick={runAutoBalance}>
              {t('nutrition.soil.grouping2.autoBalance')}
            </button>
            <button type="button" className="pat-btn" data-testid="grp-add-group" onClick={addGroup}>{t('nutrition.soil.grouping2.addGroup')}</button>
          </>
        )}
      />

      <div className="ntr-grp-body">
        {!canConfirm(blockers) && (
          <div data-testid="grp-blockers">
            <Notice tone="warn">
              <ul className="ntr-grp-blockers">
                {blockers.noGroups && <li>{t('nutrition.soil.grouping2.blk.noGroups')}</li>}
                {blockers.emptyGroupLabels.length > 0 && <li>{t('nutrition.soil.grouping2.blk.empty', { groups: blockers.emptyGroupLabels.join(', ') })}</li>}
                {blockers.noOperatorGroupLabels.length > 0 && <li>{t('nutrition.soil.grouping2.blk.noOperator', { groups: blockers.noOperatorGroupLabels.join(', ') })}</li>}
                {blockers.tooManyOperatorGroupLabels.length > 0 && <li>{t('nutrition.soil.grouping2.blk.tooManyOperators', { groups: blockers.tooManyOperatorGroupLabels.join(', ') })}</li>}
              </ul>
            </Notice>
          </div>
        )}

        <div className="ntr-grp-strip">
          {groups.map((g, i) => {
            const totals = groupTotals(g, facts);
            // Single source of truth for BOTH the option list and the "anything
            // left to add?" question — computing them separately is exactly how
            // the reported defect happened (the label only ever checked the
            // 4-cap, while the options were filtered by assignment too, so a
            // 1-operator tenant showed an enabled "Add operator…" control with
            // nothing inside it).
            const availableOperators = operators.filter((o) => !g.operators.some((x) => x.operatorId === o.operatorId));
            const atCap = g.operators.length >= 4;
            const noneLeft = atCap || availableOperators.length === 0;
            const addOperatorLabel = noneLeft
              ? (atCap ? t('nutrition.soil.grouping2.opsFull') : t('nutrition.soil.grouping2.allAssigned'))
              : (g.operators.length === 0 ? t('nutrition.soil.grouping2.addOperator') : t('nutrition.soil.grouping2.addAnother'));
            return (
              <div key={g.uid} className="ntr-grp-card" data-testid={`grp-card-${g.uid}`} style={{ borderLeftColor: groupColour(i) }}>
                <div className="ntr-grp-card__head">
                  <input
                    className="ntr-grp-card__name" data-testid={`grp-rename-${g.uid}`}
                    value={g.label} onChange={(e) => renameGroup(g.uid, e.target.value)}
                    aria-label={t('nutrition.soil.grouping2.renameGroup')}
                  />
                  <button
                    type="button" className="pat-btn pat-btn--subtle ntr-grp-card__x"
                    data-testid={`grp-remove-${g.uid}`} onClick={() => dropGroup(g.uid)}
                    aria-label={t('nutrition.soil.grouping2.removeGroup', { label: labelFor(g, i) })}
                  >×</button>
                </div>
                <div className="ntr-grp-card__totals">
                  {t('nutrition.soil.grouping2.cardTotals', {
                    kg: formatMeasure(totals.kg), trees: Math.round(totals.treeCount),
                    ha: formatMeasure(totals.areaHa), orchards: totals.orchardCount,
                  })}
                </div>
                <div className="ntr-grp-bar"><i style={{ width: `${Math.round((totals.kg / heaviestKg) * 100)}%`, background: groupColour(i) }} /></div>
                <div className="ntr-grp-card__ops">
                  {g.operators.map((o) => (
                    <span key={o.operatorId} className="ntr-grp-op" data-testid={`grp-op-${g.uid}-${o.operatorId}`}>
                      {o.name}
                      <button type="button" onClick={() => removeOperator(g.uid, o.operatorId)} aria-label={t('nutrition.soil.grouping2.removeOperator', { name: o.name })}>×</button>
                    </span>
                  ))}
                  <select
                    className="ntr-resolve-select" data-testid={`grp-op-add-${g.uid}`} value=""
                    disabled={noneLeft}
                    onChange={(e) => addOperator(g.uid, e.target.value)}
                    aria-label={addOperatorLabel}
                  >
                    <option value="">{addOperatorLabel}</option>
                    {availableOperators.map((o) => (
                      <option key={o.operatorId} value={o.operatorId}>{o.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>

        {selected.length > 0 && (
          <div className="ntr-grp-bulk" data-testid="grp-bulk-bar">
            <span>{t('nutrition.soil.grouping2.selected', { count: selected.length, kg: formatMeasure(selectedKg) })}</span>
            {hiddenSelectedCount > 0 && (
              <span data-testid="grp-bulk-hidden">{t('nutrition.soil.grouping2.hiddenSelected', { count: hiddenSelectedCount })}</span>
            )}
            <label className="ntr-grp-bulk__assign">
              {t('nutrition.soil.grouping2.assignTo')}
              <select className="ntr-resolve-select" data-testid="grp-bulk-target" value={target} onChange={(e) => setTarget(e.target.value)}>
                <option value="">{t('nutrition.soil.grouping2.chooseGroup')}</option>
                <option value={UNASSIGN_TARGET}>{t('nutrition.soil.grouping2.unassignOption')}</option>
                {groups.map((g, i) => <option key={g.uid} value={g.uid}>{labelFor(g, i)}</option>)}
              </select>
            </label>
            {/* UNASSIGN_TARGET is a non-empty sentinel, so this guard already lets
                it through — assignToGroup() then removes the selection from every
                group and adds it to none, because no real group's uid ever equals
                the sentinel. */}
            <button type="button" className="pat-btn" data-testid="grp-bulk-apply" disabled={!target} onClick={applyBulk}>
              {t('nutrition.soil.grouping2.apply')}
            </button>
            <button type="button" className="pat-btn pat-btn--subtle" data-testid="grp-bulk-clear" onClick={() => { setSelected([]); setAnchor(null); }}>
              {t('nutrition.soil.grouping2.clear')}
            </button>
          </div>
        )}

        <FilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('nutrition.soil.grouping2.searchPlaceholder')}
          chips={[
            { id: 'all', label: t('nutrition.soil.grouping2.chipAll', { count: rows.length }), active: chip === 'all' },
            { id: 'unassigned', label: t('nutrition.soil.grouping2.chipUnassigned', { count: rows.filter((r) => r.groupUid === null).length }), active: chip === 'unassigned' },
            ...cultivars.map((c) => ({ id: `${CULTIVAR_CHIP_PREFIX}${c}`, label: c, active: chip === `${CULTIVAR_CHIP_PREFIX}${c}` })),
          ]}
          onChipClick={(chipId) => setChip(chipId)}
        />

        <DataTable<OrchardRow>
          rows={sortedShown}
          rowKey={(r) => r.orchardName}
          rowClassName={(r) => (selected.includes(r.orchardName) ? 'ntr-grp-row--sel' : '')}
          emptyState={t('nutrition.soil.grouping2.noMatches')}
          sort={sort}
          onSortChange={setSort}
          columns={[
            {
              key: 'check',
              // label is ReactNode, so the select-all lives in the header where it belongs.
              label: (
                <input
                  type="checkbox"
                  data-testid="grp-check-all"
                  checked={headerCheckedAll}
                  onChange={() => setSelected(headerCheckedAll ? [] : orderedNames.slice())}
                  aria-label={t('nutrition.soil.grouping2.selectAllShown', { count: shown.length })}
                />
              ),
              render: (r) => (
                <input
                  type="checkbox"
                  data-testid={`grp-check-${r.orchardName}`}
                  checked={selected.includes(r.orchardName)}
                  onChange={(e) => onToggle(r.orchardName, (e.nativeEvent as MouseEvent).shiftKey)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t('nutrition.soil.grouping2.selectOrchard', { name: r.orchardName })}
                />
              ),
            },
            { key: 'orchardName', label: t('nutrition.soil.grouping2.col.orchard'), sortable: true },
            { key: 'cultivar', label: t('nutrition.soil.grouping2.col.cultivar'), sortable: true },
            { key: 'treeCount', label: t('nutrition.soil.grouping2.col.trees'), align: 'right', sortable: true, render: (r) => Math.round(r.treeCount) },
            { key: 'areaHa', label: t('nutrition.soil.grouping2.col.ha'), align: 'right', sortable: true, render: (r) => formatMeasure(r.areaHa) },
            { key: 'kg', label: t('nutrition.soil.grouping2.col.kg'), align: 'right', sortable: true, render: (r) => formatMeasure(r.kg) },
            {
              key: 'group',
              label: t('nutrition.soil.grouping2.col.group'),
              render: (r) => {
                const idx = groups.findIndex((g) => g.uid === r.groupUid);
                return r.groupUid
                  ? <span className="ntr-grp-chip" style={{ background: `${groupColour(idx)}1A`, color: groupColour(idx) }}>{labelFor(groups[idx], idx)}</span>
                  : <span className="ntr-grp-chip ntr-grp-chip--none">{t('nutrition.soil.grouping2.unassignedCell')}</span>;
              },
            },
          ]}
        />

        {ungrouped.length > 0 && (
          <div className="ntr-grp-ungrouped" data-testid="grp-ungrouped">
            {t('nutrition.soil.grouping2.ungrouped', { count: ungrouped.length, names: ungrouped.join(', ') })}
          </div>
        )}

        <SaveBar
          status={((): SaveBarStatus => {
            if (saveError) return { kind: 'error', message: saveError };
            if (saving) return { kind: 'saving' };
            return dirty ? { kind: 'dirty' } : null;
          })()}
          secondaryActions={[{ label: t('nutrition.soil.grouping2.saveDraft'), tone: 'subtle', onClick: onSaveDraft, disabled: saving }]}
          primaryAction={{ label: t('nutrition.soil.grouping2.confirm'), onClick: onConfirm, disabled: saving || !canConfirm(blockers) }}
        />

        {applyError && <Notice tone="danger" testId="grp-apply-error">{applyError}</Notice>}
      </div>

      {/* Shows only once the dry-run summary is in hand — the user never sees
          a set applied without first reading this. */}
      <Modal
        show={!!preview && !!summary}
        onHide={() => { setPreview(null); setSummary(null); }}
        title={t('nutrition.soil.groupSets.applyTitle', { source: preview?.label || '' })}
        size="md"
      >
        <div data-testid="grp-apply-modal">
          <Modal.Body>
            <p data-testid="grp-apply-summary">
              {t('nutrition.soil.groupSets.summary', {
                created: summary?.createdGroupCount,
                total: summary?.setGroupCount,
                assigned: summary?.assignedOrchardCount,
                unassigned: summary?.unassignedOrchardCount,
                skipped: summary?.skippedGroupCount,
              })}
            </p>
            {!!summary?.skippedGroupLabels.length && (
              <p className="ntr-grp-preview__detail">
                {t('nutrition.soil.groupSets.skippedList', { groups: summary.skippedGroupLabels.join(', ') })}
              </p>
            )}
            {!!summary?.unassignedOrchardNames.length && (
              <p className="ntr-grp-preview__detail">
                {t('nutrition.soil.groupSets.unassignedList', { orchards: summary.unassignedOrchardNames.join(', ') })}
              </p>
            )}
            {!!summary?.droppedOperatorCount && (
              <p className="ntr-grp-preview__detail">
                {t('nutrition.soil.groupSets.droppedOperators', { count: summary.droppedOperatorCount })}
              </p>
            )}
            {/* States the destructive consequence, per the U3 cancel-modal lesson:
                a confirm dialog that only asks "are you sure" is not enough. */}
            <Notice tone="warn">{t('nutrition.soil.groupSets.applyReplaces')}</Notice>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="pat-btn" onClick={() => { setPreview(null); setSummary(null); }}>
              {t('common.cancel')}
            </button>
            <button type="button" className="pat-btn pat-btn--primary" disabled={applyBusy} onClick={confirmPreview}>
              {t('nutrition.soil.groupSets.applyConfirm')}
            </button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default SoilGroupingPage;
